import { cloneDeep } from "lodash";
import { useEffect, useRef, useState } from "react";

import { getInvestor } from "../../../../services/investor.service";
import {
  defaultInvestorResource,
  defaultWireInfo,
} from "../../../../utils/constants/form.constants";
import {
  ADD_NEW_INVESTOR,
  GENERIC_ERROR_MESSAGE,
  INVESTOR_TYPE_ENTITY,
  INVESTOR_TYPE_INDIVIDUAL,
} from "../../../../utils/constants/text.constants";
import { useEffectAsync } from "../../../../utils/hooks/useEffectAsync.hook";
import { InvestorDetail } from "../../../../utils/types/investor.type";
import { GET_INVESTOR_DETAILS_ERROR } from "../InvestorList.constants";

export const useInvestorEffect = (
  investorId: string,
  showPanel: boolean,
  informationAlert: any
) => {
  const [isPanelVisible, setPanelVisible] = useState({
    right: showPanel
  });
  const [originalName, setOriginalName] = useState<string | undefined>("");
  const [investor, setInvestor] = useState<InvestorDetail>(
    cloneDeep(defaultInvestorResource)
  );
  const [originalInvestorObj, setOriginalInvestorObj] = useState<InvestorDetail>();
  const [isLoadingInvestor, setLoadingInvestor] = useState(false);
  const [isCheckingUniqueName, setCheckingUniqueName] = useState(false);
  const [isNewInvestor, setIsNewInvestor] = useState(false);

  const [title, setTitle] = useState("");

  useEffectAsync(
    async (isCanceled) => {
      if (!investorId) {
        return;
      }
      if (investorId === "new") {
        setInvestor(cloneDeep(defaultInvestorResource));
        setTitle(ADD_NEW_INVESTOR);
        setIsNewInvestor(true);
        setLoadingInvestor(false);
        return;
      } else {
        try {
          setLoadingInvestor(true);

          if (isCanceled()) return;

          const investorResponse = await getInvestor(investorId);

          setOriginalInvestorObj({ ...investorResponse });

          if (!investorResponse.wireInfo) {
            investorResponse.wireInfo = defaultWireInfo;
          }

          setInvestor({ ...investorResponse });

          setOriginalName(investorResponse.fullName);
          setTitle(investorResponse.fullName);

        } catch (e) {
          informationAlert(GET_INVESTOR_DETAILS_ERROR, "error");
          setPanelVisible({
            right: false
          });
        } finally {
          setLoadingInvestor(false);
        }
      }
    },
    [investorId]
  );

  useEffect(() => {
    setPanelVisible({
      right: showPanel
    });
  }, [showPanel]);

  return {
    investor,
    setInvestor,
    title,
    originalName,
    isLoadingInvestor,
    isNewInvestor,
    isCheckingUniqueName,
    setCheckingUniqueName,
    isPanelVisible,
    setPanelVisible,
    originalInvestorObj
  };
};

export const useInvestorValidator = (valuesInvestorDetail: any) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isRequredFieldUpdated, setRequiredfieldUpdated] = useState(false);

  useEffect(() => {
    if (
      valuesInvestorDetail.entityType === INVESTOR_TYPE_ENTITY &&
      !valuesInvestorDetail.name
    ) {
      setIsFormValid(false);
    } else if (
      valuesInvestorDetail.entityType === INVESTOR_TYPE_INDIVIDUAL &&
      (!valuesInvestorDetail.firstName || !valuesInvestorDetail.lastName)
    ) {
      setIsFormValid(false);
    } else if (
      (valuesInvestorDetail.disregardedEntity &&
        (!valuesInvestorDetail.ownerName || !valuesInvestorDetail.ownerSSN)) ||
      valuesInvestorDetail.funds.length === 0
    ) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }

    if (isRequredFieldUpdated) {
      setRequiredfieldUpdated(false);
    }
  }, [valuesInvestorDetail, isRequredFieldUpdated]);

  return {
    isFormValid,
    setRequiredfieldUpdated,
  };
};

export const useTabs = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const detailsRef = useRef<HTMLInputElement>(null);
  const fundsRef = useRef<HTMLInputElement>(null);
  const wiringRef = useRef<HTMLInputElement>(null);
  const pageBottomRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (event: any, newValue: any) => {
    setCurrentTab(newValue);
    switch (newValue) {
      case 0:
        detailsRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
      case 1:
        fundsRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
      case 2:
        wiringRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
    }
  };

  const scrollToBottom = () => {
    pageBottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return {
    currentTab,
    detailsRef,
    fundsRef,
    wiringRef,
    pageBottomRef,
    handleTabChange,
    scrollToBottom
  };
};
